import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { LatestPayload } from '../api/types';
import { marketState } from '../lib/calendar';
import { MAINNET } from '../lib/chain';
import { readFeed, readPool } from '../lib/feed';
import { dayName, dur, fmtUsd, fmtUtc } from '../lib/format';
import { readoutsFromApi, readoutsFromSnapshot, type LiveReads, type Readouts } from '../lib/readouts';
import { sessionNow, type SessionSample } from '../lib/session';
import type { Snapshot } from '../lib/snapshot';

export const FALLBACK_EVERY_MS = 60000;
const CLOCK_MS = 30000;

/** Direct reads for one symbol of the snapshot: Chainlink over JSON-RPC and DexScreener. */
async function readLive(feed: string, pair: string): Promise<LiveReads> {
  const out: LiveReads = { feed: null, pool: null };
  try { out.feed = await readFeed(feed, MAINNET); } catch { out.feed = null; }
  try { out.pool = await readPool(pair); } catch { out.pool = null; }
  return out;
}

export interface HeroProps {
  symbol: string;
  latest: LatestPayload | null;
  /** When /latest was received (ms since epoch), for the embedded session's age. */
  latestAt: number;
  session: SessionSample | null;
  snapshot: Snapshot | null;
}

export function Hero({ symbol, latest, latestAt, session, snapshot }: HeroProps) {
  // Re-render every 30 s so the durations and the market state move with the clock.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => { setTick((n) => n + 1); }, CLOCK_MS);
    return () => { clearInterval(t); };
  }, []);

  // The symbol the readouts show: the picked one when it has data, else NVDA.
  const apiSym = latest?.symbols[symbol] ? symbol : 'NVDA';
  const apiEntry = latest?.symbols[apiSym] ?? null;
  const snapSym = snapshot?.series[symbol] ? symbol : 'NVDA';
  const snapEntry = snapshot?.series[snapSym] ?? null;

  const live = useQuery({
    queryKey: ['live', snapSym],
    queryFn: () => readLive(snapEntry?.oracle_feed ?? '', snapEntry?.pair_address ?? ''),
    enabled: !apiEntry && !!snapEntry,
    refetchInterval: FALLBACK_EVERY_MS,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const now = Date.now();
  const readouts: Readouts | null = apiEntry
    ? readoutsFromApi(apiSym, apiEntry)
    : snapshot ? readoutsFromSnapshot(snapSym, snapshot, live.data ?? null) : null;

  const sample: SessionSample | null = session ?? (latest ? { payload: latest.session, at: latestAt } : null);
  const fs = sessionNow(sample, now);
  const st = marketState(new Date(now));

  const feedSession = fs.live
    ? `24 hour session ends ${dayName(fs.endsAt)} ${fmtUtc(fs.endsAt)}`
    : `since ${dayName(fs.frozenSince)} ${fmtUtc(fs.frozenSince)}, resumes ${dayName(fs.resumesAt)} ${fmtUtc(fs.resumesAt)}`;
  const lf = readouts?.lastFeed ?? null;
  const moved = lf ? `; ${lf.sym} last moved ${dur(now - lf.at.getTime())} ago at ${fmtUsd(lf.price, 2)} USD${lf.live ? '' : ' (recorded)'}` : '';

  return (
    <section className="hero">
      <div className="hero-text">
        <h1>Check a Stock Token's origin.<br />Inspect the fee policy before committing USDG.</h1>
        <p className="lede">Bellguard recomputes from chain state whether a token was deployed by the issuer's factory, in a view function any contract can call: no owner, no list, no dependence on the issuer's own lookup. That proves factory provenance, not backing, redemption or safety. Its Uniswap v4 hook refuses a pool unless the Stock Token passes that check, and states its fee rule: the pool's tier fee while the Chainlink feed is fresh, a fee that follows the measured gap while the feed is frozen, and the tier fee again once the feed prints. The fee parameters live in BellConfig behind a 24 hour timelock.</p>
        <p className="actions">
          <a className="button" href="#verify">Verify a token</a>
          <a className="button quiet" href="#quote">Quote the fee</a>
          <a className="button quiet" href="/evidence">Reviewer evidence</a>
        </p>
      </div>
      <dl className="readouts" id="readouts" aria-label="Live readings">
        <div>
          <dt>Wall Street</dt>
          <dd id="r-market" className={st.open ? 'fresh' : ''}>{st.open ? 'open' : `closed ${dur(now - st.closedAt.getTime())}`}</dd>
          <dd className="sub" id="r-market-sub">{st.open
            ? `NYSE cash session; closes ${fmtUtc(st.closesAt)}`
            : `NYSE cash session; since ${dayName(st.closedAt)} ${fmtUtc(st.closedAt)}, opens ${dayName(st.opensAt)} ${fmtUtc(st.opensAt)}`}</dd>
        </div>
        <div>
          <dt>Feed</dt>
          <dd id="r-feed" className={fs.live ? 'fresh' : 'gap'}>{fs.live ? 'live' : `frozen ${dur(now - fs.frozenSince.getTime())}`}</dd>
          <dd className="sub" id="r-feed-sub">{feedSession + moved}</dd>
        </div>
        <div>
          <dt>Pool price</dt>
          <dd id="r-pool" className={readouts?.pool.className ?? ''}>{readouts ? readouts.pool.value : 'loading'}</dd>
          <dd className="sub" id="r-pool-sub">{readouts?.pool.sub ?? ''}</dd>
        </div>
        <div>
          <dt>Gap over the feed</dt>
          <dd id="r-gap" className={readouts?.gap.className ?? 'gap'}>{readouts ? readouts.gap.value : 'loading'}</dd>
          <dd className="sub" id="r-gap-sub">{readouts?.gap.sub ?? ''}</dd>
        </div>
      </dl>
    </section>
  );
}
